/**
 * @file        src/pages/hotel/ConvenienceFee.jsx
 * @description B2C Hotel Convenience Fee CRUD (`hotel_convenience_fees`).
 *              Legacy parity columns: Update Date / Convenience Type /
 *              Convenience Amount / Status / Action, with an add-edit modal.
 *
 *              Not built on SimpleLookupPage: that factory hard-codes its column
 *              order (# → fields → Status → Updated On → Action), which does not
 *              match the reference screen (Update Date leads).
 * @author      Kinetika Labs
 * @created     2026-07-02
 * @changelog
 *   - 2026-07-02 Claude — Initial (B2C Hotel Management).
 *   - 2026-07-12 Claude — Rebuilt for legacy parity (exact columns + add/edit
 *                          modal) instead of the generic SimpleLookupPage.
 *   - 2026-08-03 Claude — Required Brand field (registry code, no default) in the
 *                          add/edit modal + Brand column — the API now demands
 *                          `brand` on create.
 *   - 2026-08-10 Claude — Added "Applies To" (All/B2C/B2B — `applicable_role`,
 *                          added to the table by migration 026 but never
 *                          exposed here) plus title/subtitle/audience props so
 *                          CorporateConvenienceFee.jsx can reuse this page fixed
 *                          to audience=b2b instead of forking it.
 *   - 2026-09-12 Claude — Spelled out the "newest active rule wins" rule in a
 *                          notice bar (several rows can sit in this list and only
 *                          one is ever charged), and REMOVED the "Applies To"
 *                          input from the modal: the screen fixes it (this page
 *                          b2c, CorporateConvenienceFee.jsx b2b), so the choice
 *                          only let an admin file a corporate fee from the retail
 *                          screen and then not find it. The column stays in the
 *                          table so an inherited 'All' row is still visible, and
 *                          an edit still sends the row's OWN role — editing a
 *                          shared 'All' rule here must not silently re-scope it.
 *   - 2026-09-15 Claude — Note now also states WHAT the fee is charged on and
 *                          WHERE it lands: on the room total, and added AFTER the
 *                          discount — so unlike flights, a discount or coupon never
 *                          comes off a hotel convenience fee.
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 *   - 2026-10-06 Claude — Domestic / International stays (migration 076), judged
 *                          on the property's country: a form field, a column, and
 *                          a notice that says a scoped fee beats one for both.
 */
import { useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import { HOTEL_FEE_BASE_NOTE } from '../../lib/pricingNotes';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Select from '../../components/Select';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import BrandSelect from '../../components/BrandSelect';
import { useBrands } from '../../api/brands';
import { get, post, patch, del } from '../../api/client';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';
import { money, dateTime } from './cells';
import { DOM_INT_SELECT_OPTIONS, domIntLabel } from '../../lib/domInt';

// hotel_convenience_fees.fee_type is ENUM('percentage','flat').
const TYPES = [{ value: 'flat', label: 'Fix' }, { value: 'percentage', label: 'Percent' }];
const ROLE_LABEL = { all: 'All (legacy)', b2c: 'B2C', b2b: 'B2B' };

function emptyForm(defaultRole) {
  return { brand: '', name: '', fee_type: 'flat', fee_value: '', min_amount: '', max_amount: '', dom_int: '', applicable_role: defaultRole, is_active: true };
}

function FeeModal({ row, defaultRole, onClose }) {
  const qc = useQueryClient();
  const isEdit = Boolean(row?.id);
  const [form, setForm] = useState(
    isEdit
      ? {
        brand: row.brand || '', name: row.name || '', fee_type: row.fee_type || 'flat', fee_value: row.fee_value ?? '',
        min_amount: row.min_amount ?? '', max_amount: row.max_amount ?? '',
        dom_int: row.dom_int || '',
        // Never 'all' — the wildcard is retired (admin migration 055) and the
        // API rejects it; a legacy row re-scopes to THIS screen's audience.
        applicable_role: row.applicable_role === 'b2b' ? 'b2b' : defaultRole,
        is_active: !!row.is_active,
      }
      : emptyForm(defaultRole),
  );

  const mut = useMutation({
    mutationFn: (body) => (isEdit ? patch(`/hotel-convenience-fees/${row.id}`, body) : post('/hotel-convenience-fees', body)),
    onSuccess: () => {
      toast.ok(isEdit ? 'Updated' : 'Added', 'Convenience fee saved.');
      qc.invalidateQueries({ queryKey: ['hotel-convenience-fees'] });
      onClose();
    },
    onError: (e) => toast.err('Could not save', e.response?.data?.error?.message || 'Validation failed'),
  });

  function submit(e) {
    e.preventDefault();
    if (!form.brand) return toast.err('Brand required', 'Select the brand this fee applies to.');
    if (!String(form.name).trim()) return toast.err('Name required', 'Give this fee a name.');
    if (form.fee_value === '' || Number(form.fee_value) < 0) return toast.err('Amount required', 'Enter a fee amount.');
    // Max Amount cannot be less than Min Amount (only when both are given).
    if (form.min_amount !== '' && form.min_amount != null && form.max_amount !== '' && form.max_amount != null
        && Number(form.min_amount) > Number(form.max_amount)) {
      return toast.err('Invalid range', 'Max Amount must be greater than or equal to Min Amount.');
    }
    // Blank optional numbers must be omitted, not sent as '' (the API coerces
    // numbers and would reject the empty string).
    const body = {
      brand: form.brand,
      name: String(form.name).trim(),
      fee_type: form.fee_type,
      fee_value: Number(form.fee_value),
      // Explicit null = both, so choosing All on edit clears a stored scope.
      dom_int: form.dom_int || null,
      applicable_role: form.applicable_role,
      is_active: !!form.is_active,
    };
    if (form.min_amount !== '' && form.min_amount != null) body.min_amount = Number(form.min_amount);
    if (form.max_amount !== '' && form.max_amount != null) body.max_amount = Number(form.max_amount);
    mut.mutate(body);
  }

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass" style={{ maxWidth: 560, width: '96%' }}>
        <div className="lk-modal-head">
          <h3>{isEdit ? 'Edit Convenience Fee' : 'Add Convenience Fee'}</h3>
          <button className="btn xs ghost" onClick={onClose}><Icon name="x" /> Close</button>
        </div>
        <form className="form-grid" onSubmit={submit}>
          <div className="field full">
            <label>Brand <span className="req">*</span></label>
            <BrandSelect value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} />
          </div>
          <div className="field full">
            <label>Name <span className="req">*</span></label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Standard Convenience Fee" />
          </div>
          <div className="field">
            <label>Convenience Type <span className="req">*</span></label>
            <Select value={form.fee_type} onChange={(v) => setForm({ ...form, fee_type: v })} options={TYPES} />
          </div>
          <div className="field">
            <label>Convenience Amount <span className="req">*</span></label>
            <input type="number" min="0" step="0.01" value={form.fee_value}
                   onChange={(e) => setForm({ ...form, fee_value: e.target.value })} placeholder="e.g. 199 or 2" />
          </div>
          <div className="field">
            <label>Min Amount</label>
            <input type="number" min="0" step="0.01" value={form.min_amount}
                   onChange={(e) => setForm({ ...form, min_amount: e.target.value })} placeholder="Applies from" />
          </div>
          <div className="field">
            <label>Max Amount</label>
            <input type="number" min="0" step="0.01" value={form.max_amount}
                   onChange={(e) => setForm({ ...form, max_amount: e.target.value })} placeholder="Applies up to" />
          </div>
          <div className="field">
            <label>Domestic / International</label>
            <Select value={form.dom_int} onChange={(v) => setForm({ ...form, dom_int: v })} options={DOM_INT_SELECT_OPTIONS} />
          </div>
          <div className="field">
            <label>Status</label>
            <label className="check">
              <input type="checkbox" checked={form.is_active}
                     onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              <span>Active</span>
            </label>
          </div>
          <div className="form-actions">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button className="btn primary" type="submit" disabled={mut.isPending}>
              <Icon name="plus" /> {mut.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Convenience Fee'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ConvenienceFee({
  title = 'Convenience Fee',
  subtitle = 'B2C Hotel Management',
  audience = 'b2c',
} = {}) {
  const qc = useQueryClient();
  const { labelOf } = useBrands();
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null); // null | {} (new) | row (edit)
  const pageSize = 25;

  const params = { page, pageSize, audience };
  const { data, isLoading } = useQuery({
    queryKey: ['hotel-convenience-fees', 'list', params],
    queryFn: () => get('/hotel-convenience-fees', { params }),
    placeholderData: keepPreviousData,
  });
  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  const removeMut = useMutation({
    mutationFn: (id) => del(`/hotel-convenience-fees/${id}`),
    onSuccess: () => {
      toast.ok('Deleted', 'Convenience fee removed.');
      qc.invalidateQueries({ queryKey: ['hotel-convenience-fees'] });
    },
    onError: (e) => toast.err('Failed', e.response?.data?.error?.message || 'Could not delete'),
  });

  const amount = (r) => (r.fee_type === 'percentage' ? `${r.fee_value}%` : money(r.fee_value));

  return (
    <>
      <PageHead
        title={title}
        subtitle={subtitle}
        actions={<button className="btn primary" onClick={() => setEditing({})}><Icon name="plus" /> Add Convenience Fee</button>}
      />

      <div className="notice-banner info">
        <span>
          Only <b>one</b> convenience fee is ever charged: of the active rules for the
          booking&apos;s brand and buyer type that match the stay, a <b>Domestic- or
          International-only</b> rule beats one for both, and between equally specific
          rules the <b>most recently added</b> wins. A stay is domestic when the hotel is
          in India. If that rule&apos;s Min/Max range does not cover the stay,
          <b> no</b> fee is charged — it does not fall back to another rule.
          {HOTEL_FEE_BASE_NOTE}
        </span>
      </div>

      <article className="panel glass">
        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>Update Date</th><th>Brand</th><th>Convenience Type</th><th>Convenience Amount</th><th>Dom / Int</th><th>Applies To</th><th>Status</th><th>Action</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={8} className="empty">No convenience fee configured yet.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id}>
                    <td className="muted">{dateTime(r.updated_at)}</td>
                    <td>{labelOf(r.brand)}</td>
                    <td>
                      {r.fee_type === 'percentage' ? 'percent' : 'fix'}
                      {r.name ? <div className="muted" style={{ fontSize: '.8em' }}>{r.name}</div> : null}
                    </td>
                    <td><b>{amount(r)}</b></td>
                    <td>{domIntLabel(r.dom_int)}</td>
                    <td>{ROLE_LABEL[r.applicable_role] || '—'}</td>
                    <td><Chip tone={r.is_active ? 'ok' : 'rej'}>{r.is_active ? 'Active' : 'Inactive'}</Chip></td>
                    <td className="row-act">
                      <button className="ib del" aria-label="Delete"
                              onClick={async () => { if (await confirm(`Delete convenience fee #${r.id}?`)) removeMut.mutate(r.id); }}>
                        <Icon name="trash" />
                      </button>
                      <button className="ib edit" aria-label="Edit" onClick={() => setEditing(r)}><Icon name="edit" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>

      {editing ? <FeeModal row={editing.id ? editing : null} defaultRole={audience === 'b2b' ? 'b2b' : 'b2c'} onClose={() => setEditing(null)} /> : null}
    </>
  );
}
